import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { serviceTypes, incoterms, documentationOptions } from '../mock';
import { API } from '../context/AuthContext';

const initialForm = {
  servicio: '',
  empresa: '',
  pais: '',
  producto: '',
  posicionArancelaria: '',
  paisOrigen: '',
  cantidad: '',
  incoterm: '',
  puertoIngreso: '',
  mercadoObjetivo: '',
  volumen: '',
  documentacion: '',
  contactoNombre: '',
  contactoEmail: '',
  contactoTelefono: '',
  observaciones: '',
};

export default function Solicitar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(location.search);
    const servicio = params.get('servicio');
    if (servicio) setForm((f) => ({ ...f, servicio }));
  }, [location.search]);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.servicio) {
      toast({ title: 'Seleccione un tipo de servicio', variant: 'destructive' });
      return;
    }
    if (!form.empresa || !form.contactoEmail || !form.contactoNombre) {
      toast({ title: 'Complete los campos obligatorios', description: 'Empresa, nombre y email de contacto son requeridos.' });
      return;
    }
    setLoading(true);
    try {
      // Send without credentials because public endpoint
      await axios.post(`${API}/contacts`, form, { withCredentials: false });
      setSubmitted(true);
      toast({ title: 'Solicitud enviada', description: 'Nos pondremos en contacto en un máximo de 48 horas.' });
    } catch (err) {
      toast({ title: 'Error al enviar', description: err.response?.data?.detail || 'Intente nuevamente.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main className="pt-[120px] pb-28 min-h-[80vh] bg-neutral-50">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-[#07301f] text-white flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="w-10 h-10" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl font-bold tracking-tight mb-4">Solicitud recibida</h1>
          <p className="text-neutral-600 text-lg mb-10">
            Gracias, {form.contactoNombre || 'su solicitud ha sido registrada'}. Nuestro equipo revisará la información y le contactará a <span className="text-[#07301f] font-medium">{form.contactoEmail}</span> en un máximo de 48 horas.
          </p>
          <Button onClick={() => navigate('/')} className="bg-[#07301f] hover:bg-[#0a4a31] text-white h-12 px-6 rounded-md">
            Volver al inicio
          </Button>
        </div>
      </main>
    );
  }

  const showImportFields = form.servicio === 'importacion';
  const showExportFields = form.servicio === 'exportacion';
  const showIntlFields = form.servicio === 'internacionalizacion';
  const showModelFields = form.servicio === 'modelo';

  return (
    <main className="pt-[120px] pb-24 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-neutral-500 hover:text-[#07301f] text-sm mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </button>

        <div className="text-[11px] tracking-[0.3em] uppercase text-[#07301f]/70 font-medium mb-4">
          Formulario de Solicitud
        </div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-5">Solicitar Cotización</h1>
        <p className="text-neutral-600 text-lg mb-12">
          Complete el formulario con los datos de su operación. Todos los campos marcados con <span className="text-red-500">*</span> son obligatorios.
        </p>

        <form onSubmit={handleSubmit} className="space-y-12">
          {/* Tipo de servicio */}
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase text-neutral-500 font-medium mb-5">
              Tipo de Servicio <span className="text-red-500">*</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {serviceTypes.map((s) => {
                const Icon = s.icon;
                const active = form.servicio === s.id;
                return (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => update('servicio', s.id)}
                    className={`rounded-xl border p-6 text-center flex flex-col items-center gap-3 hover:border-[#07301f] transition-colors ${
                      active ? 'border-[#07301f] bg-[#07301f]/5' : 'border-neutral-200 bg-white'
                    }`}
                  >
                    <Icon className="w-6 h-6 text-[#07301f]" strokeWidth={1.75} />
                    <span className="text-sm font-medium text-[#0a0a0a]">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t border-neutral-200" />

          {form.servicio && (
            <>
              <div>
                <h2 className="text-xl font-semibold mb-6">Datos de la operación</h2>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Nombre de la empresa" required>
                    <Input value={form.empresa} onChange={(e) => update('empresa', e.target.value)} placeholder="Razón social" />
                  </Field>
                  <Field label="País" required>
                    <Input value={form.pais} onChange={(e) => update('pais', e.target.value)} placeholder="País de la empresa" />
                  </Field>

                  <Field label="Producto" required>
                    <Input value={form.producto} onChange={(e) => update('producto', e.target.value)} placeholder="Descripción del producto" />
                  </Field>

                  {(showImportFields || showExportFields) && (
                    <Field label="Posición arancelaria">
                      <Input value={form.posicionArancelaria} onChange={(e) => update('posicionArancelaria', e.target.value)} placeholder="Ej: 8471.30.00" />
                    </Field>
                  )}

                  {showImportFields && (
                    <>
                      <Field label="País de origen" required>
                        <Input value={form.paisOrigen} onChange={(e) => update('paisOrigen', e.target.value)} placeholder="País del proveedor" />
                      </Field>
                      <Field label="Cantidad estimada" required>
                        <Input value={form.cantidad} onChange={(e) => update('cantidad', e.target.value)} placeholder="Unidades / toneladas" />
                      </Field>
                      <Field label="Incoterm">
                        <Select value={form.incoterm} onValueChange={(v) => update('incoterm', v)}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>
                            {incoterms.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Puerto de ingreso">
                        <Input value={form.puertoIngreso} onChange={(e) => update('puertoIngreso', e.target.value)} placeholder="Puerto destino" />
                      </Field>
                    </>
                  )}

                  {showExportFields && (
                    <>
                      <Field label="Mercado objetivo" required>
                        <Input value={form.mercadoObjetivo} onChange={(e) => update('mercadoObjetivo', e.target.value)} placeholder="País / región destino" />
                      </Field>
                      <Field label="Volumen mensual estimado">
                        <Input value={form.volumen} onChange={(e) => update('volumen', e.target.value)} placeholder="En unidades o USD" />
                      </Field>
                      <Field label="Incoterm">
                        <Select value={form.incoterm} onValueChange={(v) => update('incoterm', v)}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>
                            {incoterms.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Documentación disponible">
                        <Select value={form.documentacion} onValueChange={(v) => update('documentacion', v)}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                          <SelectContent>
                            {documentationOptions.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                    </>
                  )}

                  {showIntlFields && (
                    <>
                      <Field label="Mercado objetivo" required>
                        <Input value={form.mercadoObjetivo} onChange={(e) => update('mercadoObjetivo', e.target.value)} placeholder="País / región destino" />
                      </Field>
                      <Field label="Volumen de operación estimado">
                        <Input value={form.volumen} onChange={(e) => update('volumen', e.target.value)} placeholder="En USD anual" />
                      </Field>
                    </>
                  )}

                  {showModelFields && (
                    <Field label="Mercado objetivo" required>
                      <Input value={form.mercadoObjetivo} onChange={(e) => update('mercadoObjetivo', e.target.value)} placeholder="País / región destino" />
                    </Field>
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-6">Datos de contacto</h2>
                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Nombre y apellido" required>
                    <Input value={form.contactoNombre} onChange={(e) => update('contactoNombre', e.target.value)} placeholder="Nombre completo" />
                  </Field>
                  <Field label="Email corporativo" required>
                    <Input type="email" value={form.contactoEmail} onChange={(e) => update('contactoEmail', e.target.value)} placeholder="correo@empresa.com" />
                  </Field>
                  <Field label="Teléfono">
                    <Input value={form.contactoTelefono} onChange={(e) => update('contactoTelefono', e.target.value)} placeholder="+54 11 1234 5678" />
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="Observaciones">
                    <Textarea rows={5} value={form.observaciones} onChange={(e) => update('observaciones', e.target.value)} placeholder="Información adicional relevante para su solicitud" />
                  </Field>
                </div>
              </div>

              <div>
                <Button type="submit" disabled={loading} className="bg-[#07301f] hover:bg-[#0a4a31] text-white h-12 px-7 rounded-md font-medium">
                  {loading ? 'Enviando…' : 'Enviar Solicitud'}
                </Button>
                <p className="text-neutral-500 text-sm mt-3">
                  Al enviar, acepta que nuestro equipo se ponga en contacto con usted. Las consultas se dirigen a <span className="text-[#07301f]">info@patagonialobby.com</span>.
                </p>
              </div>
            </>
          )}
        </form>
      </div>
    </main>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-[#0a0a0a]">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      {children}
    </div>
  );
}
